
import { useEffect } from "react"; // Added useEffect
import { useKarmaData } from "./hooks/useKarmaData";
import { useKarmaRewards } from "./hooks/useKarmaRewards";
import { getCurrentLevel, getProgressToNextLevel, levelThresholds } from "./utils/karma-utils";
import { UseKarmaReturn } from "./types/karma-types";
import { useAuth } from "@/contexts/AuthContext"; // Added useAuth

export type { LeaderboardUser } from "./types/karma-types";

export const useKarma = (): UseKarmaReturn => {
  const { user } = useAuth(); // Get user from AuthContext
  const {
    karmaPoints,
    setKarmaPoints,
    activities,
    setActivities,
    loading, // This loading state is crucial
    availablePoints, // This state is updated by checkAvailablePoints
    setAvailablePoints,
    leaderboard,
    loadingLeaderboard,
    currentLevel,
    setCurrentLevel,
    fetchLeaderboard: fetchKarmaLeaderboard, // Use the correct exported name
    fetchKarmaPoints: fetchAllUserKarmaData, // Use the correct exported name
    checkAvailablePoints: checkAvailableKarmaPoints // Use the correct exported name
  } = useKarmaData();
  
  const { claimPoints } = useKarmaRewards(
    karmaPoints,
    setKarmaPoints,
    activities,
    setActivities,
    setAvailablePoints,
    currentLevel,
    setCurrentLevel,
    async () => { await fetchKarmaLeaderboard(); } // Wrap to return Promise<void>
  );

  // useEffect for auto-claiming points
  useEffect(() => {
    // Ensure data is loaded, user is present, and checkAvailableKarmaPoints function exists
    if (!loading && user && checkAvailableKarmaPoints) {
      // Attempt to auto-claim daily points if available
      if (availablePoints.daily) {
        console.log("Auto-attempting to claim daily points...");
        claimPoints('daily').catch(error => {
          console.error("Error auto-claiming daily points:", error);
          // Optionally, show a toast to the user about the error
        });
      }
      // Attempt to auto-claim weekly points if available
      if (availablePoints.weekly) {
        console.log("Auto-attempting to claim weekly points...");
        claimPoints('weekly').catch(error => {
          console.error("Error auto-claiming weekly points:", error);
          // Optionally, show a toast to the user about the error
        });
      }
    }
  }, [loading, user, availablePoints, claimPoints, checkAvailableKarmaPoints]); // Dependencies for the effect

  return {
    karmaPoints,
    activities,
    loading,
    availablePoints,
    claimPoints,
    getCurrentLevel,
    getProgressToNextLevel,
    levelThresholds,
    leaderboard,
    loadingLeaderboard,
    currentLevel,
    checkAvailablePoints: async () => { await checkAvailableKarmaPoints(); } // Wrap to return Promise<void>
  };
};
